import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ArtistArt from "../arts/artist-art";
import ToggleButton from "../buttons/toggle-button";
import Icon from "../icons/icon";
import Socials from "../socials";
import { Artist, IdentifiableOption } from "../../models/models";
import { MAX_BIO_LENGTH, useExpandBio } from "../../src/hooks";
import { useAudioPlayer } from "../../src/contexts/AudioPlayerContext";
import Marquee from "../detail-page/marquee";
import SharePopover from "../detail-page/share-popover";
import TitleBarMobile from "../modals/titlebar-mobile";
import FullScreenModalAlt from "../modals/fullscreen-modal-alt";
import useArtistBeats from "../creators/hooks/useArtistBeats";
import useArtistSongs from "../creators/hooks/useArtistSongs";
import useArtistSoundkits from "../creators/hooks/useArtistSoundkits";
import { Option } from "@/models/sorting-filtering";
import CreatorsAssetsTabs from "./creators-assets-tabs";
import { sortOptions } from "./sortOptions";
import { tabs } from "./tabs";
import ArtistCollectionsMobile from "./artist-collections-mobile";
import ArrowLeft from "../icons/lib/arrow-left";
import Location from "../icons/lib/location";
import ShareLinkMobileModal from "../modals/ShareLinkMobileModal";
import useViewType from "@/lib/hooks/useViewType";
import { Entity } from "@/lib/types/entity.type";
import useArtistSoundkitsCount from "./hooks/useArtistSoundkitsCount";
import useArtistSongsCount from "./hooks/useArtistSongsCount";
import useArtistBeatsCount from "./hooks/useArtistBeatsCount";

interface CreatorsPageContentProps {
  data: Artist;
}

const CreatorsPageContent = ({ data }: CreatorsPageContentProps) => {
  // hooks
  const router = useRouter();
  const { viewType, toggleViewType } = useViewType();
  const { bioExpanded, toggleExpandBio } = useExpandBio();
  const { setCurrentPlaylist } = useAudioPlayer();

  // states
  const [artist, setArtist] = useState(data);
  const [selectedTab, setSelectedTab] = useState<IdentifiableOption>(tabs[0]);
  const [sort, setSort] = useState<Option>(sortOptions[0]);
  const [sortVisible, setSortVisible] = useState<boolean>(false);
  const [trackListModal, setTrackListModal] = useState<boolean>(false);

  const {
    beats,
    loadMore: loadMoreBeats,
    isLoading: isBeatsLoading,
    isLoadingMore: isBeatsLoadingMore,
    reset: resetBeats,
  } = useArtistBeats({
    id: data.id,
    sorting: sort,
    pageSize: 40,
  });
  const {
    songs,
    loadMore: loadMoreSongs,
    isLoading: isSongsLoading,
    isLoadingMore: isSongsLoadingMore,
    reset: resetSongs,
  } = useArtistSongs({
    id: data.id,
    sorting: sort,
    pageSize: 40,
  });
  const {
    soundkits: sounds,
    loadMore: loadMoreSoundkits,
    isLoading: isSoundkitsLoading,
    isLoadingMore: isSoundkitsLoadingMore,
    reset: resetSoundkits,
  } = useArtistSoundkits({ id: data.id, sorting: sort, pageSize: 40 });
  const { count: beatsCount, isLoading: isBeatsCountLoading } =
    useArtistBeatsCount({
      id: data.id,
    });
  const { count: songsCount, isLoading: isSongsCountLoading } =
    useArtistSongsCount({
      id: data.id,
    });
  const { count: soundkitsCount, isLoading: isSoundkitsCountLoading } =
    useArtistSoundkitsCount({
      id: data.id,
    });

  useEffect(() => {
    if (beatsCount > 0) {
      setSelectedTab(tabs[0]);
    } else if (songsCount > 0) {
      setSelectedTab(tabs[1]);
    } else if (soundkitsCount > 0) {
      setSelectedTab(tabs[2]);
    }
  }, [beatsCount, songsCount, soundkitsCount]);

  useEffect(() => {
    if (!isBeatsLoading || !isSongsLoading || !isSoundkitsLoading) {
      if (selectedTab.name === "Beats" && beatsCount > 0) {
        setCurrentPlaylist(beats ?? []);
        setSortVisible(beats?.length === 0);
      } else if (selectedTab.name === "Songs" && songsCount > 0) {
        setCurrentPlaylist(songs ?? []);
        setSortVisible(songs?.length === 0);
      } else if (selectedTab.name === "Sound Kits" && soundkitsCount > 0) {
        setCurrentPlaylist(sounds ?? []);
        setSortVisible(sounds?.length === 0);
      }
    }
    // TODO: need to improve this effect to simplify the deps or make the deps stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeatsLoading, isSongsLoading, isSoundkitsLoading]);

  const handleChangeTab = (selectedTab: IdentifiableOption) => {
    switch (selectedTab.name) {
      case "Beats":
        setCurrentPlaylist(beats ?? []);
        setSortVisible(beats?.length === 0);
        break;
      case "Songs":
        setCurrentPlaylist(songs ?? []);
        setSortVisible(songs?.length === 0);
        break;
      case "Sound Kits":
        setCurrentPlaylist(sounds ?? []);
        setSortVisible(sounds?.length === 0);
        break;
    }
    setSelectedTab(selectedTab);
  };

  useEffect(() => {
    if (data.id) {
      setArtist(data);
      resetBeats();
      resetSongs();
      resetSoundkits();
    }
  }, [data, resetBeats, resetSongs, resetSoundkits]);

  // actions
  const closeTrackListModal = () => setTrackListModal(false);

  const handleSortClick = (sortCriteria: IdentifiableOption) => {
    const { name: criteria } = sortCriteria;
    if (criteria === sort.name) {
      return;
    }

    setSort(sortCriteria);
  };

  const getCountryOrState = () =>
    artist.location?.country === "US"
      ? artist.location?.state
      : artist.location?.country;

  const getUrl = () => {
    if (typeof window !== "undefined") {
      return window.location.href;
    }
  };

  const handleViewMore = (type: Entity) => {
    switch (type) {
      case "beats":
        loadMoreBeats();
        break;
      case "songs":
        loadMoreSongs();
        break;
      case "sounds":
        loadMoreSoundkits();
      default:
        break;
    }
  };

  const isLoading =
    isBeatsCountLoading || isSongsCountLoading || isSoundkitsCountLoading;

  return (
    <>
      <div className="">
        <button
          id="mobile-creatorDetail-button-back"
          onClick={() => router.back()}
          className="lg:hidden visible my-5"
        >
          <Icon icon={ArrowLeft} color="gray" className="h-[30px] w-[30px]" />
        </button>

        {/* Artist Info */}
        <div className="relative flex flex-col lg:flex-row items-start justify-between lg:pb-10 pt-0 ">
          <div className="flex flex-col lg:flex-row lg:justify-start items-center w-full lg:w-[85%] ">
            {/* Artist image */}
            <div className="flex w-1/3 lg:w-1/5 min-w-[150px] md:min-w-[200px] max-w-[250px] my-4">
              <ArtistArt
                id="creator-creatorArt-edit"
                noHover
                artist={artist}
                className="w-full"
              />
            </div>

            <div className="flex flex-col items-center lg:items-start lg:pl-6 w-full overflow-hidden">
              <div className="flex max-w-full items-center md:pl-0 mb-2">
                <div className="flex max-w-full h-full truncate gap-5">
                  <Marquee>
                    <h4 className="flex text-7xl font-semibold leading-normal max-w-full md:max-w-[90%]">
                      {artist?.name || "Artist Name"}
                    </h4>
                  </Marquee>
                </div>
              </div>

              {/* Location */}
              {artist?.location?.city &&
                artist?.location?.state &&
                artist?.location?.country && (
                  <div className="flex items-center space-x-3 mt-3 lg:mt-0">
                    <Icon
                      icon={Location}
                      className="h-[18px] w-[18px] lg:h-[25px] lg:w-[25px]"
                    />
                    <a className="underline text-sm lg:text-lg">
                      {`${artist.location.city}, ${getCountryOrState()}`}
                    </a>
                  </div>
                )}

              {/* Bio */}
              <p className="text-gray hidden lg:block md:mt-3 text-center md:text-start w-full md:w-auto my-5 md:my-0 px-5 md:px-10">
                {artist?.bio && artist?.bio !== ""
                  ? artist?.bio.length < MAX_BIO_LENGTH || bioExpanded
                    ? artist.bio
                    : artist?.bio.slice(0, MAX_BIO_LENGTH).concat("...")
                  : `This artist does not have a bio yet.`}
                {artist?.bio && artist.bio.length > MAX_BIO_LENGTH && (
                  <span className="text-gray">
                    <button
                      id="creatorDetail-button-expandBio"
                      onClick={toggleExpandBio}
                      className="underline ml-2 text-gray"
                    >
                      {bioExpanded ? "see less" : "see more"}
                    </button>
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Socials */}
          <div className="flex items-center justify-center lg:justify-end w-full lg:w-fit -mt-5">
            <Socials
              className=" lg:w-full flex justify-center lg:justify-end my-10"
              links={artist.socialMedia}
            />
            <SharePopover
              id="creatorDetail-button-share"
              className="mt-1 pl-3 border-l-1 border-backgroundLight hidden lg:flex"
              artistSocials={artist.socialMedia}
            />

            <div className="flex items-center border-l-1 border-backgroundLight pl-3">
              <ShareLinkMobileModal
                title="Share"
                text="Check this out on Lutely"
                url={getUrl()}
                className="lg:hidden"
              />
            </div>
          </div>
        </div>

        {/* Artist collections */}
        <div className="hidden lg:block">
          <CreatorsAssetsTabs
            loading={isLoading}
            beatsCount={beatsCount}
            songsCount={songsCount}
            soundkitsCount={soundkitsCount}
            isBeatsLoading={isBeatsLoading}
            isSongsLoading={isSongsLoading}
            isSoundkitsLoading={isSoundkitsLoading}
            isBeatsLoadingMore={isBeatsLoadingMore}
            isSongsLoadingMore={isSongsLoadingMore}
            isSoundkitsLoadingMore={isSoundkitsLoadingMore}
            beats={beats}
            songs={songs}
            soundkits={sounds}
            selectedTab={selectedTab}
            onSortChange={handleSortClick}
            onTabChange={handleChangeTab}
            onViewMore={handleViewMore}
          />
        </div>

        {/* Artist Colections (Mobile) */}
        <ArtistCollectionsMobile
          visible={!trackListModal}
          beats={beats}
          songs={songs}
          soundkits={sounds}
          beatsCount={beatsCount}
          songsCount={songsCount}
          soundkitsCount={soundkitsCount}
          isBeatsLoading={isBeatsLoading}
          isSongsLoading={isSongsLoading}
          isSoundkitsLoading={isSoundkitsLoading}
          onTabClick={(tab) => {
            setSelectedTab(tab);
            setTrackListModal(true);
          }}
        />
      </div>

      <FullScreenModalAlt
        open={trackListModal}
        onClose={closeTrackListModal}
        className="visible md:hidden"
      >
        <div className="px-6 pb-10 md:pb-0">
          <TitleBarMobile
            allowBack
            onBack={closeTrackListModal}
            title={`${selectedTab.name}`}
            right={
              sortVisible && (
                <div className="flex items-center gap-4">
                  <ToggleButton
                    id="creator-toggle-gridList"
                    value={viewType === "list"}
                    onChange={toggleViewType}
                    mobile
                    className="relative"
                  />
                </div>
              )
            }
            className="sticky top-0 z-20"
          />
          <CreatorsAssetsTabs
            loading={isLoading}
            beatsCount={beatsCount}
            songsCount={songsCount}
            soundkitsCount={soundkitsCount}
            isBeatsLoading={isBeatsLoading}
            isSongsLoading={isSongsLoading}
            isSoundkitsLoading={isSoundkitsLoading}
            isBeatsLoadingMore={isBeatsLoadingMore}
            isSongsLoadingMore={isSongsLoadingMore}
            isSoundkitsLoadingMore={isSoundkitsLoadingMore}
            beats={beats}
            songs={songs}
            soundkits={sounds}
            selectedTab={selectedTab}
            onSortChange={handleSortClick}
            onTabChange={handleChangeTab}
            onViewMore={handleViewMore}
          />
        </div>
      </FullScreenModalAlt>
    </>
  );
};

export default CreatorsPageContent;
